/* 🔴 W46 — WHAT A SET-TABLE CELL WILL ACCEPT. His rules, 2026-09-22, in his own words:
 *
 *   "make reps max 3 digits, and kg need to be able to have a comma. but not really a comma, only
 *    the dot can be used as a comma, nothing else allowed. so kg max digits should be 3, but if a
 *    comma is there then 2 before and 2 after the comma max. then anything with time like rest or
 *    time based exercises should be able to type time so not more than 4 digits. and of course
 *    time logic so not be able to type more than 59:59."
 *
 * ⚠️ **THIS IS A DATA-CORRECTNESS FIX, NOT INPUT POLISH, AND THE MEASUREMENT IS WHY.** DRIVEN in a
 * real browser before any of it was written, typing one character at a time through the native
 * setter: **`2.5` typed into a KG cell ended up as `25`**, and so did `2,5`. A coach prescribing
 * two and a half kilos silently got twenty-five.
 *
 * 🔴 **THE CAUSE WAS STRUCTURAL AND IS THE REASON THIS FILE EXISTS.** The cell was a CONTROLLED
 * input whose value came back from the model through `Number(t.replace(",", "."))`. The instant
 * `2.` was typed, `Number("2.")` is `2`, the model held `2`, and the re-render wrote `"2"` back
 * into the field — **the separator could never survive a keystroke**, so no decimal weight could be
 * entered by typing at all. A `maxLength` would not have touched it.
 *
 * ⚠️ **SO THE RULE IS A PREDICATE OVER THE WHOLE TEXT, NOT A PARSER.** The field keeps its own text
 * while it is being typed and only hands a number to the model when the text is a complete one —
 * which is the shape `RestField` already used and the piece worth copying. `accepts` answers "may
 * the field hold this string", and everything else follows from it:
 *   · typing happens natively, so the caret lands where the coach put it, with no mask arithmetic;
 *   · a keystroke that would break a rule is REVERTED, which is his "when we reach the right most
 *     digit not be able to type more" without needing to know where the right-most digit is;
 *   · a half-typed value (`2.`, `1:`) is ACCEPTED as text and simply yields no number yet.
 *
 * ⚠️ **AND A COMMA IS REFUSED RATHER THAN TRANSLATED**, which reverses what the old cell did on
 * purpose: his word is *"only the dot can be used as a comma, nothing else allowed."*
 *
 * 🔴 **ONE RESIDUE, NAMED RATHER THAN DESIGNED AROUND, BECAUSE IT IS HIS CALL AND NOT MINE.**
 * MEASURED after the fix: typing `2,5` leaves the field holding **`25`**. The comma keystroke is
 * correctly refused, and the `5` then lands beside the `2`. So a coach with the European habit can
 * still produce a ten-times value — the difference from the old bug is that the field now SHOWS
 * `25` while they type it, rather than showing `2` and storing `25`.
 * **Translating a comma to a dot would remove the hazard and is exactly what his sentence
 * forbids**, so it is not done here. If he would rather have the translation, that is a one-line
 * change to `accepts` and a reversal to record with both quotes — it is not a thing to decide for
 * him on the strength of a hazard he may already have weighed.
 */

/* W136 B2 S2b — `setTime` is A SET'S TIME (his word 2026-10-05: *"longer than an hour is acceptable so forget about this rule"*;
   Fable's ruling): up to `23:59:59` (86,399 s, inside the live CHECK). Colons `m:ss` · `mm:ss` · `h:mm:ss` · `hh:mm:ss`; bare digits
   up to SIX, read from the right as ss, mm, hh (ss and mm ≤ 59, hh ≤ 23) — so `13000` = 1:30:00 and `6000` stays refused (an hour
   is `10000`). A 3- or 5-digit state is judged, as `time`'s 3-digit state is, by whether ANY completion is valid; a 4-digit state is
   judged as a whole `mmss`. `time` is unchanged and stays REST's grammar (59:59 — the kind splits). Every text `time` accepts,
   `setTime` accepts with the same seconds (gate `w136-b2-s2b-times-over-an-hour` executes it over the shared case table). */
export type CellKind = "reps" | "weight" | "time" | "setTime";

const le = (digits: string | undefined, max: number): boolean => digits !== undefined && digits !== "" && Number(digits) <= max;

/** A set's time as its cell SHOWS it when not being typed into — `m:ss` under an hour, `h:mm:ss` from one (the Swift twin is
 *  `TypedCell.timeText`). Re-typeable by `setTime` as shown. */
export function timeText(seconds: number | null): string {
  if (seconds === null) return "";
  const pad = (n: number) => String(n).padStart(2, "0");
  if (seconds >= 3600) return `${Math.floor(seconds / 3600)}:${pad(Math.floor((seconds % 3600) / 60))}:${pad(seconds % 60)}`;
  return `${Math.floor(seconds / 60)}:${pad(seconds % 60)}`;
}

/** `setTime`: may the cell hold this text while it is typed? */
function acceptsSetTime(text: string): boolean {
  if (/^\d{1,6}$/.test(text)) {
    const n = text.length;
    if (n <= 2) return true;
    if (n === 3) return le(text.slice(1), 59) || (le(text.slice(0, 2), 59) && le(text[2], 5));
    if (n === 4) return le(text.slice(0, 2), 59) && le(text.slice(2), 59);
    if (n === 5) {
      const whole = le(text.slice(1, 3), 59) && le(text.slice(3), 59);
      const prefix = le(text.slice(0, 2), 23) && le(text.slice(2, 4), 59) && le(text[4], 5);
      return whole || prefix;
    }
    return le(text.slice(0, 2), 23) && le(text.slice(2, 4), 59) && le(text.slice(4), 59);
  }
  const three = /^(\d{1,2}):(\d{2}):(\d{0,2})$/.exec(text);
  if (three !== null) return le(three[1], 23) && le(three[2], 59) && (three[3] === "" || le(three[3], 59));
  const two = /^(\d{1,2}):(\d{0,2})$/.exec(text);
  if (two !== null) return le(two[1], 59) && (two[2] === "" || le(two[2], 59));
  /* `1:3` on the way to `1:30:00` is the two-part form above; `1:30:` (a third part begun) is the three-part form. */
  return false;
}

/** `setTime`: the seconds a COMPLETE text means, or `null` while it is still being typed. */
function valueOfSetTime(text: string): number | null {
  const three = /^(\d{1,2}):(\d{2}):(\d{2})$/.exec(text);
  if (three !== null) return Number(three[1]) * 3600 + Number(three[2]) * 60 + Number(three[3]);
  const two = /^(\d{1,2}):(\d{2})$/.exec(text);
  if (two !== null) return Number(two[1]) * 60 + Number(two[2]);
  if (!/^\d+$/.test(text)) return null;
  if (text.length <= 2) return Number(text);
  const ss = Number(text.slice(-2)), mm = Number(text.slice(-4, -2)), hh = text.length > 4 ? Number(text.slice(0, -4)) : 0;
  if (ss > 59 || mm > 59 || hh > 23) return null;
  return hh * 3600 + mm * 60 + ss;
}

/** Every state the field may hold WHILE TYPING, including incomplete ones. */
export function accepts(kind: CellKind, text: string): boolean {
  if (text === "") return true;
  if (kind === "setTime") return acceptsSetTime(text);
  if (kind === "reps") return /^\d{1,3}$/.test(text);
  if (kind === "weight") {
    /* No separator: three digits. His "kg max digits should be 3". */
    if (/^\d{1,3}$/.test(text)) return true;
    /* With one: two before and two after, and a trailing dot is a legal thing to be halfway
       through typing. ⚠️ A LEADING dot is refused — `.5` is a weight nobody writes on a
       whiteboard, and accepting it would make `0.5` and `.5` two spellings of one number. */
    return /^\d{1,2}\.\d{0,2}$/.test(text);
  }
  /* TIME — "not more than 4 digits" and "not be able to type more than 59:59".
     ⚠️ THE CEILING IS A RULE ABOUT THE PAIR, NOT ABOUT EACH HALF, and that is why it is checked
     here rather than per keystroke: `7` then `5` are both valid keystrokes and `75` seconds is
     not a time. Refusing at the KEYSTROKE (rather than at blur) is the choice, because his whole
     sentence is about what the field will let you type. */
  if (/^\d{1,4}$/.test(text)) {
    /* Bare digits are `mmss` being typed: `9`, `93`, `130`, `5959`.
       🔴 THIS USED TO READ THE LAST TWO DIGITS AND NOTHING ELSE, AND IT BROKE HIS RULE IN BOTH
       DIRECTIONS — MEASURED 2026-09-22 while building the Swift twin:
         · HIS OWN MAXIMUM COULD NOT BE TYPED. Typing 5,9,5,9 stopped dead at `"59"` = 59
           seconds, because the third keystroke makes `595`, whose last two digits are `95`.
           The state is only invalid as a FINISHED value; as a prefix of `5959` it is perfectly
           on the way to 59:59, which is the exact number he named as the ceiling.
         · AND VALUES ABOVE IT COULD. The MINUTES half was never checked at all, so `6000` was
           accepted and meant 60:00 (3,600 s), and `9959` meant 99:59 (5,999 s) — the precise
           thing *"not be able to type more than 59:59"* forbids.
       ⚠️ SO A 3-DIGIT STATE IS JUDGED BY WHETHER ANY COMPLETION CAN BE VALID, which is what a
       predicate over a half-typed string has to mean. `abc` is allowed when it is already a
       whole time (`bc` <= 59, e.g. `130` = 1:30) OR when it can still become one (`ab` <= 59 and
       `c` <= 5, so some `abcd` has `cd` <= 59). `599` is refused on both counts, correctly: no
       fourth digit rescues a seconds value starting `9`. */
    if (text.length <= 2) return true;
    if (text.length === 3) {
      const asWhole = Number(text.slice(1)) <= 59;
      const asPrefix = Number(text.slice(0, 2)) <= 59 && Number(text[2]) <= 5;
      return asWhole || asPrefix;
    }
    return Number(text.slice(0, 2)) <= 59 && Number(text.slice(2)) <= 59;
  }
  const m = /^(\d{1,2}):(\d{0,2})$/.exec(text);
  if (m === null) return false;
  return Number(m[1]) <= 59 && (m[2] === "" || Number(m[2]) <= 59);
}

/** The number a COMPLETE text means, or `null` when it is still being typed. */
export function valueOf(kind: CellKind, text: string): number | null {
  if (!accepts(kind, text)) return null;
  if (text === "") return null;
  if (kind === "reps") return Number(text);
  if (kind === "weight") return /\.$/.test(text) ? null : Number(text);
  if (kind === "setTime") return valueOfSetTime(text);
  const m = /^(\d{1,2}):(\d{2})$/.exec(text);
  if (m !== null) return Number(m[1]) * 60 + Number(m[2]);
  if (!/^\d+$/.test(text)) return null;
  if (text.length <= 2) return Number(text);
  /* ⚠️ `accepts` NOW ADMITS A 3-DIGIT STATE THAT IS ONLY VALID AS A PREFIX (`595` on the way to
     `5959`), so this has to refuse what is not yet a whole time. Returning 5×60+95 for `595`
     would hand the model a number for something the coach is still in the middle of typing —
     the same class of defect as the one this file was written to fix, one function along. */
  const mm = Number(text.slice(0, -2));
  const ss = Number(text.slice(-2));
  if (mm > 59 || ss > 59) return null;
  return mm * 60 + ss;
}
